import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { GS_COMPONENTS } from '../tokens';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface GsWizardStep {
  id: string;
  title: string;
  content: ReactNode;
  /** Retourne false pour bloquer l'avance ou la soumission finale. Defaut : true. */
  canProceed?: () => boolean;
}

export interface GsWizardDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  steps: GsWizardStep[];
  onComplete: () => void;
  previousLabel?: string;
  nextLabel?: string;
  completeLabel?: string;
  cancelLabel?: string;
  /** Libelle pendant l'attente (ex. soumission en cours). */
  pendingLabel?: string;
  isLoading?: boolean;
}

/**
 * Dialog multi-etapes generique — navigation prev/next, validation par etape,
 * soumission finale via onComplete. Primitives du parc (overlay ConfirmDialog +
 * focus-trap GsDrawer), boutons GS_COMPONENTS.
 *
 * L'etat d'etape vit dans un corps interne monte uniquement quand `open` :
 * fermer puis rouvrir remonte a l'etape 1 sans effet de re-seed.
 */
export function GsWizardDialog(props: GsWizardDialogProps) {
  if (!props.open || props.steps.length === 0) return null;
  return <GsWizardDialogBody {...props} />;
}

function GsWizardDialogBody({
  onClose,
  title,
  steps,
  onComplete,
  previousLabel = 'Retour',
  nextLabel = 'Suivant',
  completeLabel = 'Terminer',
  cancelLabel = 'Annuler',
  pendingLabel = 'Envoi en cours…',
  isLoading = false,
}: GsWizardDialogProps) {
  const [current, setCurrent] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    const timer = window.setTimeout(() => {
      const el = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      el?.focus();
    }, 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(timer);
    };
  }, [onClose, current]);

  const multiStep = steps.length > 1;
  const isLast = current === steps.length - 1;
  const step = steps[current];
  const canAdvance = step.canProceed?.() ?? true;
  const pending = isLoading;

  function goNext() {
    if (!canAdvance || pending) return;
    setCurrent((c) => Math.min(c + 1, steps.length - 1));
  }

  function goBack() {
    if (pending) return;
    setCurrent((c) => Math.max(c - 1, 0));
  }

  function complete() {
    if (!canAdvance || pending) return;
    onComplete();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/40 dark:bg-black/60 animate-dialog-backdrop" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gs-wizard-dialog-title"
        className="relative w-full sm:max-w-lg mx-0 sm:mx-4 max-h-[calc(100vh-2rem)] overflow-y-auto
                   bg-white dark:bg-slate-900
                   rounded-t-2xl sm:rounded-2xl
                   border-0 sm:border border-slate-100 dark:border-slate-700
                   shadow-xl p-6 animate-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <h3
            id="gs-wizard-dialog-title"
            className="text-base font-semibold text-slate-800 dark:text-slate-100"
          >
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={cancelLabel}
            className={GS_COMPONENTS.btnClose}
          >
            <X size={15} aria-hidden />
          </button>
        </div>

        {multiStep ? (
          <div className="flex flex-col gap-2 mb-4">
            <p className="text-meta text-slate-500 dark:text-slate-400" aria-live="polite">
              Etape {current + 1} sur {steps.length} — {step.title}
            </p>
            <div className="flex items-center gap-1.5" aria-hidden>
              {steps.map((s, i) => (
                <span
                  key={s.id}
                  className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ${
                    i <= current ? 'bg-brand-500' : 'bg-slate-200 dark:bg-slate-700'
                  }`}
                />
              ))}
            </div>
          </div>
        ) : null}

        <div className="min-h-32 mb-4">{step.content}</div>

        <div className="flex items-center justify-between gap-2 pt-1">
          {multiStep ? (
            <button
              type="button"
              onClick={goBack}
              disabled={current === 0 || pending}
              className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5 disabled:opacity-50`}
            >
              <ArrowLeft size={14} aria-hidden />
              {previousLabel}
            </button>
          ) : (
            <span />
          )}

          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} disabled={pending} className={GS_COMPONENTS.btnSecondary}>
              {cancelLabel}
            </button>
            {isLast ? (
              <button
                type="button"
                onClick={complete}
                disabled={!canAdvance || pending}
                className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5 disabled:opacity-50`}
              >
                <Check size={14} aria-hidden />
                {pending ? pendingLabel : completeLabel}
              </button>
            ) : (
              <button
                type="button"
                onClick={goNext}
                disabled={!canAdvance || pending}
                className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5 disabled:opacity-50`}
              >
                {nextLabel}
                <ArrowRight size={14} aria-hidden />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
